
    import {Options, Prop, Ref, Vue} from 'vue-property-decorator';
    import {LocationAutocomplete} from '@malt/location-autocomplete';

    import {Address} from '@/common/components/location/types/Address';
    import {LocationEvents} from '@/common/components/location/types/LocationEvents';
    import LocationValidation from '@/common/components/location/validation/LocationValidation';
    import type {BlockedCountriesList} from '@/common/repositories/blocked-countries/BlockedCountriesList';
    import {Components} from '@malt/joy-entrypoint';

    @Options({})
    export default class InputLocation extends Vue {
        @Prop({type: Array, default: [], required: true})
        blockedCountriesList!: BlockedCountriesList;
        @Prop({type: Object, required: false})
        defaultAddress?: Address;

        locationValidation = new LocationValidation();
        locationAutocomplete: LocationAutocomplete;

        @Ref('inputLocation')
        inputLocation!: Components.JoyInput & HTMLElement;

        address?: Address;
        error = false;
        focus = false;

        blockedCountry: string | null = null;

        async mounted(): Promise<void> {
            this.locationAutocomplete = new LocationAutocomplete({
                input: this.$refs.inputLocation,
                defaultValue: this.defaultAddress,
                layer: 'LOCALITY',
                focusPoint: window.GEOLOCATION_FOCUS_POINT,
                autocompleteUrl: this.$pageContext.localisationUrl,
                onSelect: (selectedAddress: any) => {
                    const address = this.formatAddress(selectedAddress);
                    this.updateAddress(address);
                    this.validate();
                },
            });
        }

        beforeUnmount(): void {
            this.locationAutocomplete.destroy();
        }

        validate(): void {
            if (this.inputLocation.value.trim().length === 0) {
                this.error = true;
                this.blockedCountry = null;
                this.$emit(LocationEvents.inputLocationValidated, this.address, this.error);
                return;
            }

            this.error = !this.locationValidation.isValid(this.address) || !this.isNotABlockedCountry(this.address);
            this.$emit(LocationEvents.inputLocationValidated, this.address, this.error);
        }

        isNotABlockedCountry(address?: Address): boolean {
            this.blockedCountry = null;

            const isValidCountry = this.locationValidation.isValidCountry(this.blockedCountriesList!, address);
            if (!isValidCountry) {
                this.blockedCountry = this.address!.countryCode;
            }

            return isValidCountry;
        }

        emitFocus(): void {
            this.$emit('focus');
        }

        get isValid(): boolean {
            return !this.error;
        }

        get isBlockedCountry(): boolean {
            return !!this.blockedCountry;
        }

        get blockCountryCode(): string | null {
            return this.blockedCountry;
        }

        private formatAddress(address: any): Address {
            return {
                city: address?.city || null,
                countryCode: address?.countryCode || null,
                country: address?.country || null,
                administrativeAreaLevel1: address?.administrativeAreaLevel1 || null,
                administrativeAreaLevel1Code: address?.administrativeAreaLevel1 || null,
                administrativeAreaLevel2: address?.administrativeAreaLevel2 || address?.city || null,
                administrativeAreaLevel2Code: null,
                administrativeAreaLevel3: null,
                administrativeAreaLevel3Code: null,
                administrativeAreaLevel4: null,
                administrativeAreaLevel4Code: null,
                lon: address?.loc.lon || null,
                lat: address?.loc.lat || null,
                formattedAddress: address?.formattedAddress || null,
            };
        }

        private updateAddress(newAddress: Address): void {
            this.address = Object.assign({}, newAddress);
            this.$emit(LocationEvents.inputLocationUpdated, newAddress);
        }
    }
